import React, { useState } from 'react';
import { useSelector } from 'react-redux';
import { Route } from 'react-router-dom';
import { Subscription, Query } from 'react-apollo';
import { FormControl, InputLabel, Select, MenuItem } from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import Box from '@material-ui/core/Box';
import Card from '@material-ui/core/Card';
import Grid from '@material-ui/core/Grid';
import CardHeader from '@material-ui/core/CardHeader';
import Switch from '@material-ui/core/Switch';
import FormControlLabel from '@material-ui/core/FormControlLabel';
import Spin from 'components/common/Spin';
import { LISTEN_USER_ROOMS, GET_ROOMS } from 'graphql/chat';
import Conversation from './components/Conversation';
import { ROLES } from 'constant/userTypes';
import ChannelList from './components/ChannelsList';
import SearchBox from './components/SearchBox';

const useStyles = makeStyles((theme) => ({
  headerRoot: {
    flexWrap: 'wrap',
  },
  cardAction: {
    'margin': 0,
    flex: 'unset',
    '& .MuiFormControlLabel-label': {
      fontSize: 14,
    },
  },
  select: {
    paddingTop: 0,
    paddingBottom: 4,
    fontSize: '14px',
    borderRadius: 4,
  },
  formControl: {
    margin: theme.spacing(1),
    minWidth: 120,
  },
  label: {
    paddingBottom: theme.spacing(1),
    color: '#D2D7E3',
    fontWeight: 600,
    textTransform: 'uppercase',
  },
  item: {
    fontSize: 'inherit',
  },
}));

const sortOptions = [
  {
    label: 'Unread First',
    value: { unread: 'desc' },
  },
  {
    label: 'Recent',
    value: {},
  },
  {
    label: 'Name',
    value: { student: { user_first_name: 'asc' } },
  },
];

const sortAllChannelOptions = [
  {
    label: 'Unread First',
    value: { users_unread_counts_aggregate: { count: 'desc' } },
  },
  {
    label: 'Recent',
    value: { messages_aggregate: { max: { created_at: 'desc_nulls_last' } } },
  },
  {
    label: 'Name',
    value: { student: { user_first_name: 'asc' } },
  },
];

const Chat = ({
  match: {
    params: { roomId },
  },
}) => {
  const classes = useStyles();
  const user = useSelector((s) => s.user.user);
  const [query, setQuery] = useState('');
  const [sortParams, setsortParams] = useState({ unread: 'desc' });
  const [sortallChannelsParams, setsortallChannelsParams] = useState({
    student: { user_first_name: 'asc' },
  });
  const [allChannels, setAllChannels] = useState(false);

  const handleChange = (e) => {
    !allChannels
      ? setsortParams(e.target.value)
      : setsortallChannelsParams(e.target.value);
  };

  return (
    <Box>
      <Grid container spacing={2}>
        <Grid item md={5} sm={12}>
          <Card>
            <CardHeader
              title="Channels"
              titleTypographyProps={{
                variant: 'subtitle1',
              }}
              classes={{
                action: classes.cardAction,
                root: classes.headerRoot
              }}
              action={ ROLES.PARENT !== user.type &&
                <>
                  {!allChannels && (
                    <FormControl className={classes.formControl}>
                      <InputLabel
                        className={classes.label}
                        shrink
                        id="sort-label"
                      >
                        Sort By
                      </InputLabel>
                      <Select
                        displayEmpty
                        classes={{
                          select: classes.select,
                        }}
                        id="sort"
                        name="sort"
                        value={sortParams}
                        onChange={handleChange}
                        renderValue={(val) =>
                          sortOptions.find((s) => s.value === val)?.label ||
                          'Select'
                        }
                      >
                        {sortOptions.map((i) => (
                          <MenuItem
                            classes={{ root: classes.item }}
                            key={i.value}
                            value={i.value}
                          >
                            {i.label}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  )}
                  {allChannels && (
                    <FormControl className={classes.formControl}>
                      <InputLabel
                        className={classes.label}
                        shrink
                        id="sort-label-all"
                      >
                        Sort By
                      </InputLabel>
                      <Select
                        displayEmpty
                        classes={{
                          select: classes.select,
                        }}
                        id="sortAll"
                        name="sortAll"
                        value={sortallChannelsParams}
                        onChange={handleChange}
                        renderValue={(val) =>
                          sortAllChannelOptions.find((s) => s.value === val)
                            ?.label || 'Select'
                        }
                      >
                        {sortAllChannelOptions.map((i) => (
                          <MenuItem
                            classes={{ root: classes.item }}
                            key={i.value}
                            value={i.value}
                          >
                            {i.label}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  )}
                  {user.type === ROLES.ADMIN && (
                    <FormControlLabel
                      value={true}
                      checked={allChannels}
                      onChange={(e) => setAllChannels(e.target.checked)}
                      control={<Switch color="primary" />}
                      label="All Channels"
                      labelPlacement="start"
                    />
                  )}
                </>
              }
            />
            <Box paddingLeft={2} paddingRight={2}>
              <SearchBox onSearch={setQuery} />
            </Box>
            {allChannels ? (
              <Query
                query={GET_ROOMS}
                variables={{ query: `%${query}%`, sortallChannelsParams }}
              >
                {({ data, loading }) => {
                  if (loading) return <Spin linear spinning />;

                  return (
                    <ChannelList allRooms roomId={roomId} rooms={data.rooms} />
                  );
                }}
              </Query>
            ) : (
              <Subscription
                subscription={LISTEN_USER_ROOMS}
                variables={{ userId: user.id, query: `%${query}%`, sortParams }}
              >
                {({ data, loading, error }) => {
                  if (error) return error.message;
                  if (loading) return <Spin linear spinning />;

                  return <ChannelList rooms={data.rooms} roomId={roomId} />;
                }}
              </Subscription>
            )}
          </Card>
        </Grid>
        <Grid item md={7} sm={12}>
          <Box marginBottom={{xs:14,sm:0}}>
          <Route path="/inbox/messages/:roomId" component={Conversation} />
          </Box>
        </Grid>
      </Grid>
    </Box>
  );
};

export default Chat;
